import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import { VideoLoopPlayer } from "../../components/automation/VideoLoopPlayer";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

const VideoLoopPage = () => {
  const theme = useTheme();
  const { token } = useAuthContext();
  const { id } = useParams();
  const [videoLinks, setVideoLinks] = useState([]); // State to store the video links
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0); // State to track the current video index
  const [loading, setLoading] = useState(true); // State to track loading status
  const [isVideoError, setIsVideoError] = useState(false);
  const [error, setError] = useState(""); // State to track errors if any

  useEffect(() => {
    const fetchVideoLinks = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/video-loop/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const videoIDs = response.data.videocollection
          ? response.data.videocollection
              .split(",")
              .filter((id) => id && id !== "0")
              .map((id) => Number(id))
          : [];

        const videos = [];
        for (const videoID of videoIDs) {
          const videoResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/video/${videoID}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (
            videoResponse.data.vimeolink !== "YouTube" &&
            videoResponse.data.vimeolink !== "Vimeo"
          )
            videos.push(
              `https://d2nut6gc5qzbiv.cloudfront.net/34481519/` +
                videoResponse.data.videolink
            );
          else if (videoResponse.data.vimeolink === "YouTube")
            videos.push(
              `https://www.youtube.com/embed/${videoResponse.data.videolink}?autoplay=1&enablejsapi=1&origin=${window.location.origin}`
            );
          else if (videoResponse.data.vimeolink === "Vimeo")
            videos.push(
              `https://player.vimeo.com/video/${videoResponse.data.videolink}`
            );
        }

        setVideoLinks(videos); // Store the fetched video links
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch videos. Please try again later.");
        setLoading(false);
      }
    };

    fetchVideoLinks();
  }, [id, token]);

  const handleVideoEnd = () => {
    // Move to the next video in the array
    setCurrentVideoIndex((prevIndex) =>
      prevIndex + 1 < videoLinks.length ? prevIndex + 1 : 0
    );
  };

  if (isVideoError) {
    return (
      <Typography
        sx={{ fontSize: "24px", color: theme.palette.text.secondary }}
      >
        No Video Found
      </Typography>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="center"
      sx={{ width: "100vw", height: "100vh" }} // Ensure the video takes the full viewport
    >
      {loading && (
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
      )}
      {/* Show loading message */}
      {error && <Typography color="error">{error}</Typography>}{" "}
      {/* Show error message */}
      {videoLinks.length > 0 && (
        <VideoLoopPlayer
          key={currentVideoIndex} // Add a key to force re-render when the index changes
          src={videoLinks[currentVideoIndex]} // Use the current video link
          onEnded={handleVideoEnd} // Triggered when the video ends
          onError={() => setIsVideoError(true)} // Handle video errors
        />
      )}
    </Box>
  );
};

export default VideoLoopPage;
